import { Group, Switch, Text, Stack, Box, Divider, Button } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { Coding } from '@medplum/fhirtypes';
import { ProviderRole } from '@bariendo/lib/constants';
import { DayAvailability } from './types';
import { TimeRangeInput } from '@/pages/SchedulingPage/components/TimeRangeInput';
import { AppointmentTypesInput } from '@/pages/SchedulingPage/components/AppointmentTypesInput';

interface DayAvailabilityRowProps {
  day: DayAvailability;
  dayLabel: string;
  onToggleDay: () => void;
  onAddTimeRange: () => void;
  onRemoveTimeRange: (index: number) => void;
  onUpdateTimeRange: (index: number, field: 'startTime' | 'endTime', value: string) => void;
  onUpdateAppointmentTypes: (timeRangeIndex: number, appointmentTypes: Coding[]) => void;
  providerRoleCode?: ProviderRole;
}

export function DayAvailabilityRow({
  day,
  dayLabel,
  onToggleDay,
  onAddTimeRange,
  onRemoveTimeRange,
  onUpdateTimeRange,
  onUpdateAppointmentTypes,
  providerRoleCode,
}: DayAvailabilityRowProps) {
  if (!day.enabled) {
    return (
      <Group gap="xs">
        <Switch checked={day.enabled} onChange={onToggleDay} label={dayLabel} size="sm" withThumbIndicator={false} />
        <Text size="sm" c="dimmed">
          Unavailable
        </Text>
      </Group>
    );
  }

  return (
    <Group gap="md" align="flex-start" wrap="nowrap">
      <Switch checked={day.enabled} onChange={onToggleDay} label={dayLabel} size="sm" withThumbIndicator={false} />
      <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
        {day.timeRanges.map((timeRange, index) => {
          const isLastTimeRange = index === day.timeRanges.length - 1;

          return (
            <Box key={index}>
              <TimeRangeInput
                timeRange={timeRange}
                onChange={(field, value) => onUpdateTimeRange(index, field, value)}
                onRemove={() => onRemoveTimeRange(index)}
              />
              <Box mt="sm">
                <AppointmentTypesInput
                  timeRange={timeRange}
                  onChange={(appointmentTypes) => onUpdateAppointmentTypes(index, appointmentTypes)}
                  collapsible
                  selectAllByDefault
                  providerRoleCode={providerRoleCode}
                />
              </Box>
              {!isLastTimeRange && <Divider my="sm" />}
            </Box>
          );
        })}

        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconPlus size={16} />}
          onClick={onAddTimeRange}
          style={{ color: '#246A7A', alignSelf: 'flex-start' }}
        >
          Add new time range
        </Button>
      </Stack>
    </Group>
  );
}
